<script setup lang="ts">
  import { computed, inject, onBeforeUnmount, onMounted, reactive, ref, watch, watchEffect } from 'vue'
  import { pendingRefocusKey } from '@/shared/models/pendingRefocus'
  import {
    Form,
    Input,
    InputNumber,
    Select,
    SelectOption,
    Checkbox,
    Button,
    AutoComplete
  } from 'ant-design-vue'
  import { AnalysisConfigSchema } from '../models/AnalysisConfigSchema'
  import { AnalysisConfigField } from '../models/AnalysisConfigField'
  import { AnalysisConfigFieldType } from '../models/AnalysisConfigFieldType'
  import { AnalysisConfigService } from '../services/AnalysisConfigService'
  import { debounceTime, Subject } from 'rxjs'
  import { parseSmartValue, DEFAULT_INPUT_DEBOUNCE_MS } from '@/utils'
  import { MinusSquare, PlusSquare } from 'lucide-vue-next'
  import JsonAnalysisConfigConfiguration from './JsonAnalysisConfigConfiguration.vue'
  import { AnalysisConfigJsonConfigurationService } from '../services/AnalysisConfigJsonConfigurationService'
  import SelectOrCreateAnalysisConfigConfiguration from './SelectOrCreateAnalysisConfigConfiguration.vue'
  import ColumnMultiSelectOrCreateAnalysisConfigConfiguration from './ColumnMultiSelectOrCreateAnalysisConfigConfiguration.vue'
  import { CompressOutlined, ExpandOutlined } from '@ant-design/icons-vue'
  import AnalysisConfigHelpPopover from '../help/components/AnalysisConfigHelpPopover.vue'
  import RegexAnalysisConfigConfiguration from './RegexAnalysisConfigConfiguration.vue'
  import ColorAnalysisConfigConfiguration from './ColorAnalysisConfigConfiguration.vue'
  import StringTemplateBuilder from '@/shared/StringTemplateBuilder/components/StringTemplateBuilder.vue'
  import ArrayAnalysisConfigBuilder from './ArrayAnalysisConfigBuilder.vue'
  import SelectXorTextAnalysisConfigConfiguration from './SelectXorTextAnalysisConfigConfiguration.vue'
  import { AnalysisConfigOption } from '../models/AnalysisConfigOption'
  import SelectXorSelectAnalysisConfigConfiguration from './SelectXorSelectAnalysisConfigConfiguration.vue'
  import { AnalysisConfig } from '../models/AnalysisConfig'
  import { AnalysisConfigsUiService } from '../services/AnalysisConfigsUiService'
  import { useWindowSize } from '@/composables/useWindowSize'

  interface IObjectAnalysisConfigBuilderProps {
    configSchemas: Array<AnalysisConfigSchema>
    analysisConfigField: AnalysisConfigField
    rootAnalysisConfig: AnalysisConfig | null
    modelValue: Record<string, any>
    availableColumns: Array<{ value: string; label: string }>
    configurationLevel: number
    isCondensedLayout: boolean
    globalCollapsed?: { collapsed: boolean }
    currentPath?: string[]
  }

  interface IObjectAnalysisConfigBuilderEmits {
    (e: 'update:modelValue', value: Record<string, any>): void
  }

  const props = defineProps<IObjectAnalysisConfigBuilderProps>()
  const emit = defineEmits<IObjectAnalysisConfigBuilderEmits>()

  const formRef = ref()
  const modelValue = reactive<Record<string, any>>({ ...(props.modelValue ?? {}) })
  const configuration = ref<Record<string, any>>({})
  const isCollapsed = ref<boolean>(false)
  const globalCollapsed = ref<{ collapsed: boolean }>(props.globalCollapsed ?? { collapsed: false })
  const { controlSize } = useWindowSize()

  // Initialize configuration with default values
  const initializeConfiguration = () => {
    if (props.analysisConfigField.field_type !== AnalysisConfigFieldType.Object) return
    // Support nested object structures
    if (
      (props.analysisConfigField.item_schema == null && props.analysisConfigField.fields == null) ||
      (props.analysisConfigField.item_schema != null &&
        props.analysisConfigField.item_schema.field_type !== AnalysisConfigFieldType.Object)
    ) {
      throw new Error(
        'ObjectAnalysisConfigBuilder requires an Object type with fields or child item_schema of type Object'
      )
    }

    if (props.analysisConfigField.fields != null) {
      configuration.value = AnalysisConfigService.initializeAnalysisConfigFieldConfiguration(
        props.analysisConfigField.fields!
      )
    } else if (props.analysisConfigField.item_schema != null) {
      configuration.value = AnalysisConfigService.initializeAnalysisConfigConfigurationForField(
        props.analysisConfigField.item_schema
      )
    }
  }

  const handleConfigurationChange = () => {
    if (props.analysisConfigField) {
      emit('update:modelValue', configuration.value)
    }
  }

  // placeholder for future type-change handling
  // @ts-expect-error
  const handleConfigTypeChanged = (cfgType: string, value: {}) => {
    // Intentionally left as no-op: type conversion for nested object config not currently supported
  }

  const pendingRefocus = inject(pendingRefocusKey)

  let isUnmounting = false

  const onInputChange = (newValue: string, fieldName: string) => {
    const activeEl = document.activeElement as HTMLInputElement | null
    if (pendingRefocus) {
      pendingRefocus.value = {
        levelFieldKey: `${props.configurationLevel}:${fieldName}`,
        cursor: activeEl?.selectionStart ?? null
      }
    }
    inputValueChanged$.next({ value: newValue, field: fieldName })
  }

  const onInputBlur = (fieldKey: string) => {
    if (isUnmounting) return
    if (pendingRefocus?.value?.levelFieldKey === `${props.configurationLevel}:${fieldKey}`) {
      pendingRefocus.value = null
    }
  }

  const inputValueChanged$ = new Subject<{ field: string; value: string }>()
  const inputValueChangedSubscription = inputValueChanged$
    .pipe(debounceTime(DEFAULT_INPUT_DEBOUNCE_MS))
    .subscribe(({ field, value }) => {
      const parsedValue = parseSmartValue(value)
      configuration.value[field] = parsedValue
      handleConfigurationChange()
    })

  // Watch for model changes
  watch(
    () => props.modelValue,
    (newVal) => {
      // Sync reactive modelValue into configuration
      Object.assign(modelValue, newVal ?? {})
      configuration.value = { ...(newVal ?? {}) }
    },
    { immediate: true }
  )

  watch(
    () => props.analysisConfigField,
    (newField) => {
      if (newField) {
        if (Object.keys(configuration.value).length === 0) {
          initializeConfiguration()
          handleConfigurationChange()
        }
      }
    },
    { immediate: true }
  )

  watchEffect(() => {
    const newVal = props.globalCollapsed
    if (newVal) {
      globalCollapsed.value = newVal
      isCollapsed.value = !!newVal.collapsed
    }
  })

  const toggleCollapse = () => {
    isCollapsed.value = !isCollapsed.value
  }

  const toggleGlobalCollapse = () => {
    globalCollapsed.value = { collapsed: !isCollapsed.value }
    isCollapsed.value = !isCollapsed.value
  }

  // If only single field, do not use grid css
  const simpleFieldCount = computed(() =>
    props?.analysisConfigField?.fields
      ? props.analysisConfigField.fields.filter(
          (f: any) =>
            f.field_type !== AnalysisConfigFieldType.Object &&
            f.field_type !== AnalysisConfigFieldType.Array &&
            f.field_type !== AnalysisConfigFieldType.Json
        ).length
      : 0
  )

  const shouldShowGlobalCollapseButton = computed(() => {
    return (
      props.analysisConfigField?.fields?.findIndex(
        (x: AnalysisConfigField) =>
          x.field_type === AnalysisConfigFieldType.Array ||
          x.field_type === AnalysisConfigFieldType.Object ||
          x.field_type === AnalysisConfigFieldType.Json
      ) !== -1
    )
  })

  const shouldShowFieldInformation = (field: AnalysisConfigField): boolean => {
    return (
      field.field_type !== AnalysisConfigFieldType.Object &&
      field.field_type !== AnalysisConfigFieldType.Json
    )
  }

  const isObjectWithNestedObject = computed(
    () => props.analysisConfigField.fields == null && props.analysisConfigField.item_schema != null
  )

  const checkboxSizeClass = computed(() => {
    if (controlSize.value === 'small') return 'checkbox-sm'
    if (controlSize.value === 'large') return 'checkbox-lg'
    return 'checkbox-md'
  })

  const displayField = computed(() => {
    const mappedField = props.analysisConfigField?.display_field as
      | string
      | string[]
      | null
      | undefined
    if (mappedField == null) return undefined

    const fields = props.analysisConfigField?.fields ?? []

    const valueForName = (name: string) => {
      const matchingField = fields.find((x) => x.name === name)
      if (!matchingField) return undefined
      const val = configuration.value?.[matchingField.name]
      return val === undefined ? undefined : val
    }

    if (typeof mappedField === 'string') {
      return valueForName(mappedField)
    }

    if (Array.isArray(mappedField)) {
      for (const name of mappedField) {
        const v = valueForName(name)
        if (v) return v
      }
    }

    return undefined
  })

  onMounted(() => {
    if (pendingRefocus?.value) {
      const { levelFieldKey, cursor } = pendingRefocus.value
      let el = document.querySelector(`[data-field="${levelFieldKey}"]`) as HTMLElement | null
      if (el && el.tagName !== 'INPUT') el = el.querySelector('input') ?? el
      const inputEl = el as HTMLInputElement | null
      if (inputEl?.tagName === 'INPUT') {
        inputEl.focus()
        if (cursor !== null) {
          try { inputEl.setSelectionRange(cursor, cursor) } catch {}
        }
      }
      pendingRefocus.value = null
    }
  })

  onBeforeUnmount(() => {
    isUnmounting = true
    inputValueChangedSubscription?.unsubscribe()
  })
</script>

<template>
  <div v-if="isObjectWithNestedObject" class="object-analysisconfig-builder-grid-span">
    <ObjectAnalysisConfigBuilder
      :configuration-level="props.configurationLevel"
      :root-analysis-config="props.rootAnalysisConfig"
      :available-columns="props.availableColumns"
      v-model="configuration[props.analysisConfigField.item_schema!.name]"
      :analysis-config-field="props.analysisConfigField.item_schema!"
      @update:modelValue="handleConfigurationChange"
      :config-schemas="configSchemas"
      :is-condensed-layout="props.isCondensedLayout"
    />
  </div>

  <div v-else>
    <div
      v-if="analysisConfigField"
      :class="[
        'border',
        'analysis-config-configuration',
        'h-full',
        'w-full',
        {
          'child-config-configuration': props.configurationLevel > 1,
          '!bg-[var(--card)] dark:!bg-[var(--card-light)]': props.configurationLevel % 2 !== 0,
          '!bg-[var(--card-light)] dark:!bg-[var(--card)]': props.configurationLevel % 2 === 0
        }
      ]"
    >
      <div class="flex h-full flex-col">
        <!-- Header -->
        <div class="horizontal-rule mb-4 border-b pb-1">
          <div class="flex">
            <div>
              <h3 class="mb-1 text-lg font-semibold">
                Configure: {{ analysisConfigField.display_name }}
                <span v-if="displayField != null">{{ `(${displayField})` }}</span>
              </h3>
            </div>

            <div class="ml-auto flex" v-if="props.configurationLevel > 1">
              <a-tooltip placement="top">
                <template #title>Toggle Collapse</template>
                <Button
                  style="padding: 3px; border: none"
                  type="text"
                  :size="controlSize"
                  class="flex items-center justify-center"
                  @click="toggleCollapse"
                  aria-label="Toggle Collapse"
                >
                  <PlusSquare
                    v-if="isCollapsed"
                    class="text-base"
                    style="line-height: 1; margin: 0"
                  />
                  <MinusSquare v-else class="text-base" style="line-height: 1; margin: 0" />
                </Button>
              </a-tooltip>

              <a-tooltip v-if="shouldShowGlobalCollapseButton" placement="top">
                <template #title>{{ isCollapsed ? 'Expand All' : 'Collapse All' }}</template>
                <Button
                  style="padding: 3px; border: none"
                  type="text"
                  size="large"
                  class="flex items-center justify-center"
                  @click="toggleGlobalCollapse"
                  aria-label="Toggle Collapse All"
                >
                  <ExpandOutlined
                    v-if="isCollapsed"
                    class="rotate-90 rounded-sm border-gray-300 p-1 text-base"
                    style="line-height: 1; margin: 0"
                  />
                  <CompressOutlined
                    v-else
                    class="rotate-90 rounded-sm border-gray-300 p-1 text-base"
                    style="line-height: 1; margin: 0"
                  />
                </Button>
              </a-tooltip>
            </div>
          </div>
        </div>

        <!-- Configuration Form -->
        <div
          :class="[
            'flex-1',
            'overflow-y-auto',
            { 'collapsed-analysisconfig-configuration': isCollapsed }
          ]"
        >
          <Form ref="formRef" :model="configuration" layout="vertical">
            <div
              :class="{
                'object-analysisconfig-fields-grid': true,
                expanded: !props.isCondensedLayout
              }"
            >
              <div
                v-for="field in analysisConfigField.fields"
                :key="field.name"
                class="object-analysisconfig-form-field-grid"
                :class="{
                  'array-analysisconfig-builder-grid-span':
                    field.field_type === AnalysisConfigFieldType.Array,
                  'object-analysisconfig-builder-grid-span':
                    field.field_type === AnalysisConfigFieldType.Object,
                  'json-analysisconfig-configuration-grid-span':
                    field.field_type === AnalysisConfigFieldType.Json,
                  'single-field-grid-span':
                    simpleFieldCount <= 1 &&
                    field.field_type !== AnalysisConfigFieldType.Object &&
                    field.field_type !== AnalysisConfigFieldType.Array &&
                    field.field_type !== AnalysisConfigFieldType.Json,
                  '!hidden': !AnalysisConfigsUiService.isFieldVisibleFromRoot(
                    field,
                    props.rootAnalysisConfig,
                    props.currentPath ? [...props.currentPath, field.name] : [field.name]
                  )
                }"
              >
                <div class="flex">
                  <div v-if="shouldShowFieldInformation(field)" class="min-w-0 flex-1">
                    <label class="table-field-label mb-1 block text-sm font-medium">
                      {{ field.display_name }}
                      <span v-if="field.required" class="text-red-500">*</span>
                    </label>
                    <p v-if="field.description" class="table-field-label mb-2 text-xs">
                      {{ field.description }}
                    </p>
                  </div>

                  <div
                    v-if="field.help_content != null && field.help_content?.length > 0"
                    class="m1-auto flex justify-end"
                  >
                    <AnalysisConfigHelpPopover
                      :content-type="field.help_content"
                      :title="`${field.display_name} Help`"
                    />
                  </div>
                </div>

                <!-- Text Input -->
                <Input
                  v-if="field.field_type === AnalysisConfigFieldType.Text"
                  :value="configuration[field.name]"
                  @update:value="(val: string) => onInputChange(val, field.name)"
                  @blur="() => onInputBlur(field.name)"
                  :data-field="`${props.configurationLevel}:${field.name}`"
                  :placeholder="field.placeholder || ''"
                  :readonly="field.readonly"
                  :disabled="field.readonly"
                  :size="controlSize"
                />

                <!-- Number Input -->
                <InputNumber
                  v-else-if="field.field_type === AnalysisConfigFieldType.Number"
                  v-model:value="configuration[field.name]"
                  :placeholder="field.placeholder || ''"
                  :size="controlSize"
                  @change="handleConfigurationChange"
                  :disabled="field.readonly"
                  :readonly="field.readonly"
                  class="w-full"
                />

                <!-- Regex Input -->
                <RegexAnalysisConfigConfiguration
                  v-else-if="field.field_type === AnalysisConfigFieldType.RegularExpression"
                  v-model="configuration[field.name]"
                  :required="field.required"
                  :regex="field.field_type_args?.regex_pattern"
                  :data-type="field.field_type_args?.regex_data_type"
                  :placeholder="field.placeholder || ''"
                  :error-message="field.field_type_args?.regex_description"
                  @update:modelValue="handleConfigurationChange"
                />

                <!-- Select Dropdown -->
                <Select
                  v-else-if="field.field_type === AnalysisConfigFieldType.Select"
                  v-model:value="configuration[field.name]"
                  :placeholder="`Select ${field.display_name.toLowerCase()}`"
                  @change="handleConfigurationChange"
                  :size="controlSize"
                  :disabled="field.readonly"
                  :readonly="field.readonly"
                  class="w-full"
                  showSearch
                  allowClear
                >
                  <SelectOption
                    v-for="option in field.options"
                    :key="option.value"
                    :value="option.value"
                  >
                    <span v-html="option.label"></span>
                  </SelectOption>
                </Select>

                <!-- Select Dropdown JSON Payload -->
                <Select
                  v-else-if="field.field_type === AnalysisConfigFieldType.SelectJson"
                  :value="JSON.stringify(configuration[field.name])"
                  :placeholder="`Select ${field.display_name.toLowerCase()}`"
                  @update:value="
                    (val: string) => {
                      try {
                        const parsed = JSON.parse(val)
                        configuration[field.name] = parsed
                        handleConfigurationChange()
                      } catch (e) {
                        console.error('Failed to parse selected JSON value', e)
                      }
                    }
                  "
                  :size="controlSize"
                  :disabled="field.readonly"
                  :readonly="field.readonly"
                  class="w-full"
                  showSearch
                  allowClear
                >
                  <SelectOption
                    v-for="option in field.options"
                    :key="JSON.stringify(option.value)"
                    :value="JSON.stringify(option.value)"
                  >
                    <span v-html="option.label"></span>
                  </SelectOption>
                </Select>

                <!-- Select Dropdown Or Create -->
                <SelectOrCreateAnalysisConfigConfiguration
                  v-else-if="field.field_type === AnalysisConfigFieldType.SelectOrCreate"
                  v-model="configuration[field.name]"
                  :placeholder="`Select or enter new ${field.display_name.toLowerCase()}`"
                  :options="field.options || []"
                  :disabled="field.readonly"
                  :readonly="field.readonly"
                  @update:modelValue="handleConfigurationChange"
                />

                <!-- Multi-Select -->
                <Select
                  v-else-if="
                    field.field_type === AnalysisConfigFieldType.MultiSelect ||
                    field.field_type === AnalysisConfigFieldType.ColumnMultiSelect
                  "
                  v-model:value="configuration[field.name]"
                  mode="multiple"
                  :placeholder="`Select ${field.display_name.toLowerCase()}`"
                  @change="handleConfigurationChange"
                  :size="controlSize"
                  :disabled="field.readonly"
                  :readonly="field.readonly"
                  class="w-full"
                  showSearch
                  allowClear
                >
                  <SelectOption
                    v-if="field.field_type === AnalysisConfigFieldType.MultiSelect"
                    v-for="option in field.options"
                    :key="option.value"
                    :value="option.value"
                  >
                    <span v-html="option.label"></span>
                  </SelectOption>

                  <SelectOption
                    v-if="field.field_type === AnalysisConfigFieldType.ColumnMultiSelect"
                    v-for="option in props.availableColumns"
                    :key="option.value"
                    :value="option.value"
                  >
                    <span v-html="option.label"></span>
                  </SelectOption>
                </Select>

                <!-- Boolean Checkbox -->
                <Checkbox
                  v-else-if="field.field_type === AnalysisConfigFieldType.Boolean"
                  @change="handleConfigurationChange"
                  v-model:checked="configuration[field.name]"
                  :disabled="field.readonly"
                  :readonly="field.readonly"
                  :class="checkboxSizeClass"
                >
                  {{ field.display_name }}
                </Checkbox>

                <!-- Column Select -->
                <Select
                  v-else-if="field.field_type === AnalysisConfigFieldType.ColumnSelect"
                  v-model:value="configuration[field.name]"
                  :placeholder="`Select ${field.display_name.toLowerCase()}`"
                  @change="handleConfigurationChange"
                  :size="controlSize"
                  :disabled="field.readonly"
                  :readonly="field.readonly"
                  class="w-full"
                  showSearch
                  allowClear
                >
                  <SelectOption
                    v-for="column in props.availableColumns"
                    :key="column.value"
                    :value="column.value"
                  >
                    <span v-html="column.label"></span>
                  </SelectOption>
                </Select>

                <!-- Column Select or Create -->
                <AutoComplete
                  v-else-if="field.field_type === AnalysisConfigFieldType.ColumnSelectOrCreate"
                  v-model:value="configuration[field.name]"
                  :options="props.availableColumns"
                  :placeholder="
                    field.placeholder || `Select existing column or enter new column name`
                  "
                  :size="controlSize"
                  class="w-full"
                  @change="handleConfigurationChange"
                  showSearch
                  allowClear
                />

                <!-- Multi Column Select Or Create -->
                <ColumnMultiSelectOrCreateAnalysisConfigConfiguration
                  v-else-if="field.field_type === AnalysisConfigFieldType.ColumnMultiSelectOrCreate"
                  v-model="configuration[field.name]"
                  :placeholder="`Select or enter new ${field.display_name.toLowerCase()} values`"
                  :available-columns="props.availableColumns"
                  @update:modelValue="handleConfigurationChange"
                />

                <!-- Color Picker -->
                <ColorAnalysisConfigConfiguration
                  v-else-if="field.field_type === AnalysisConfigFieldType.ColorPicker"
                  v-model="configuration[field.name]"
                  :place-holder="field.placeholder || 'Select color'"
                  @update:modelValue="handleConfigurationChange"
                />

                <!-- Array Type -->
                <ArrayAnalysisConfigBuilder
                  v-else-if="field.field_type === AnalysisConfigFieldType.Array"
                  :root-analysis-config="props.rootAnalysisConfig"
                  :configuration-level="props.configurationLevel"
                  :available-columns="props.availableColumns"
                  v-model="configuration[field.name]"
                  :analysis-config-field="field"
                  :key="analysisConfigField?.name"
                  @update:modelValue="handleConfigurationChange"
                  @child-config-type-changed="handleConfigTypeChanged"
                  :config-schemas="configSchemas"
                  :is-condensed-layout="props.isCondensedLayout"
                  :global-collapsed="globalCollapsed"
                  :current-path="
                    props.currentPath ? [...props.currentPath, field.name] : [field.name]
                  "
                />

                <!-- Object Type -->
                <ObjectAnalysisConfigBuilder
                  v-else-if="field.field_type === AnalysisConfigFieldType.Object"
                  :root-analysis-config="props.rootAnalysisConfig"
                  :configuration-level="props.configurationLevel + 1"
                  :available-columns="props.availableColumns"
                  v-model="configuration[field.name]"
                  :analysis-config-field="field"
                  @update:modelValue="handleConfigurationChange"
                  :config-schemas="configSchemas"
                  :is-condensed-layout="props.isCondensedLayout"
                  :global-collapsed="globalCollapsed"
                  :current-path="
                    props.currentPath ? [...props.currentPath, field.name] : [field.name]
                  "
                />

                <!-- String Template -->
                <StringTemplateBuilder
                  v-else-if="field.field_type === AnalysisConfigFieldType.StringTemplate"
                  :configuration-level="props.configurationLevel + 1"
                  :available-columns="props.availableColumns"
                  :delimiter_start="field.field_type_args?.delimiter_start"
                  :delimiter_end="field.field_type_args?.delimiter_end"
                  v-model="configuration[field.name]"
                  :place-holder="field.placeholder"
                  @update:modelValue="handleConfigurationChange"
                  :is-condensed-layout="props.isCondensedLayout"
                  :global-collapsed="globalCollapsed"
                />

                <!-- Select XOR Text -->
                <SelectXorTextAnalysisConfigConfiguration
                  v-else-if="field.field_type === AnalysisConfigFieldType.SelectXorText"
                  v-model="configuration[field.name]"
                  :default-model-value="field.default_value"
                  v-model:alternative-model-value="
                    configuration[field.field_type_args?.xor_alt_field_name]
                  "
                  @update:modelValue="handleConfigurationChange"
                  @update:alternativeModelValue="handleConfigurationChange"
                  :inactive-field-default-value="
                    field.field_type_args?.xor_inactive_field_default_value
                  "
                  :toggle-label="field.field_type_args?.xor_toggle_label"
                  :options="
                    field.field_type_args?.xor_use_column_options === true
                      ? props.availableColumns
                      : (field.options as AnalysisConfigOption[])
                  "
                  :place-holder="field.placeholder"
                />

                <!-- Select XOR Text -->
                <SelectXorSelectAnalysisConfigConfiguration
                  v-else-if="field.field_type === AnalysisConfigFieldType.SelectXorSelect"
                  v-model="configuration[field.name]"
                  :default-model-value="field.default_value"
                  v-model:alternative-model-value="
                    configuration[field.field_type_args?.xor_alt_field_name]
                  "
                  @update:modelValue="handleConfigurationChange"
                  @update:alternativeModelValue="handleConfigurationChange"
                  :inactive-field-default-value="
                    field.field_type_args?.xor_inactive_field_default_value
                  "
                  :toggle-label="field.field_type_args?.xor_toggle_label"
                  :options="
                    field.field_type_args?.xor_use_column_options === true
                      ? props.availableColumns
                      : (field.options as AnalysisConfigOption[])
                  "
                  :place-holder="field.placeholder"
                  :alternative-options="
                    field.field_type_args?.xor_alt_field_use_column_options === true
                      ? props.availableColumns
                      : (field.field_type_args?.xor_alt_field_options as AnalysisConfigOption[])
                  "
                />

                <!-- Json Type (Custom Config) -->
                <JsonAnalysisConfigConfiguration
                  v-else-if="field.field_type === AnalysisConfigFieldType.Json"
                  :analysis-config-schemas="props.configSchemas"
                  :available-columns="props.availableColumns"
                  v-model="configuration"
                  :editor-schema="
                    AnalysisConfigJsonConfigurationService.getJsonEditorSchemaForAnalysisConfig()
                  "
                  :place-holder="field.placeholder || 'Configure Json'"
                  :configuration-level="props.configurationLevel"
                  @analysis-config-type-changed="handleConfigTypeChanged"
                  @update:modelValue="handleConfigurationChange"
                  :allow-type-change="true"
                />

                <!-- Default fallback for unknown field types -->
                <JsonAnalysisConfigConfiguration
                  v-else
                  :analysis-config-schemas="props.configSchemas"
                  :available-columns="props.availableColumns"
                  v-model="configuration[field.name]"
                  :editor-schema="
                    AnalysisConfigJsonConfigurationService.getJsonEditorSchemaForField(field)
                  "
                  :place-holder="field.placeholder || 'Configure Json'"
                  :configuration-level="props.configurationLevel"
                  @update:modelValue="handleConfigurationChange"
                  :allow-type-change="false"
                />
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .collapsed-analysisconfig-configuration {
    display: none;
  }

  .analysis-config-configuration {
    border: 1px solid #0000002e;
    padding: 10px;
  }

  .horizontal-rule {
    border-bottom-color: #0000001f;
  }

  .object-analysisconfig-fields-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
  }

  @media (min-width: 1100px) {
    .object-analysisconfig-fields-grid:not(.expanded) {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .object-analysisconfig-form-field-grid {
    width: 100%;
    min-height: 90px;
    max-height: 130px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    overflow: hidden;
  }

  .array-analysisconfig-builder-grid-span,
  .object-analysisconfig-builder-grid-span,
  .json-analysisconfig-configuration-grid-span {
    grid-column: 1 / -1 !important;
    min-height: unset;
    max-height: unset;
    overflow: visible;
  }

  .table-field-label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    display: block;
  }

  .radio-icon-center {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    font-size: 18px;
  }
</style>
